import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { handleApiError } from '@/lib/api/axios'
import { authenticateEnterprisePasskey, credentialToServerFormat, isWebAuthnSupported } from '@/lib/security/webauthn-enterprise'
import { devicesApi, type Device } from '../api'

/**
 * "Confirm it's you": prove a sign-in device you already have, before adding or removing one.
 *
 * A session is not that proof - a session can be stolen. The server gives a five-minute token for a
 * right authenticator code or an answer from one of the user's own passkeys, and every change to
 * the devices is made on that token (Andrew, 13 Oct 2026: adding a device needs a factor the user
 * already has).
 */
export function StepUpDialog({ open, devices, onClose, onSteppedUp, strongOnly = false }: {
    open: boolean
    /** The user's active devices: which kinds of proof to offer. */
    devices: Device[]
    onClose: () => void
    /** `strong`: the proof was a passkey or security key, not an authenticator code. */
    onSteppedUp: (token: string, expiresInSeconds: number, strong: boolean) => void
    /** A high-risk action (Stage 7): an authenticator code will not do, so it is not offered. */
    strongOnly?: boolean
}) {
    const { t } = useTranslation()
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const hasTotp = !strongOnly && devices.some((d) => d.kind === 'totp')
    const hasPasskey = devices.some((d) => d.kind === 'passkey') && isWebAuthnSupported()

    const done = (result: { stepUpToken: string; expiresInSeconds: number }, strong: boolean) => {
        setCode('')
        setError(null)
        onSteppedUp(result.stepUpToken, result.expiresInSeconds, strong)
    }
    const run = async (work: () => Promise<{ stepUpToken: string; expiresInSeconds: number }>, strong: boolean) => {
        setBusy(true)
        setError(null)
        try {
            done(await work(), strong)
        } catch (e) {
            setError(handleApiError(e))
        } finally {
            setBusy(false)
        }
    }
    const withCode = () => run(() => devicesApi.stepUpTotp(code), false)
    const withPasskey = () => run(async () => {
        const options = await devicesApi.stepUpPasskeyBegin()
        const credential = await authenticateEnterprisePasskey(options)
        return devicesApi.stepUpPasskeyComplete(credentialToServerFormat(credential))
    }, true)

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
            <DialogContent className="sm:max-w-md" data-testid="step-up-dialog">
                <DialogHeader>
                    <DialogTitle>{t('devices.stepUp.title')}</DialogTitle>
                    <DialogDescription>{t(strongOnly ? 'highRisk.needStrong' : 'devices.stepUp.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {hasPasskey && (
                        <Button type="button" className="w-full" onClick={withPasskey} disabled={busy}>
                            <KeyRound className="mr-2 h-4 w-4" />
                            {t('devices.stepUp.usePasskey')}
                        </Button>
                    )}
                    {hasTotp && (
                        <form
                            className="space-y-2"
                            onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(code)) withCode() }}
                        >
                            <Label htmlFor="step-up-code">{t('devices.stepUp.codeLabel')}</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="step-up-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                />
                                <Button type="submit" variant={hasPasskey ? 'outline' : 'default'} disabled={busy || !/^\d{6}$/.test(code)}>
                                    {t('devices.stepUp.confirm')}
                                </Button>
                            </div>
                        </form>
                    )}
                    {!hasTotp && !hasPasskey && (
                        <p className="text-sm text-muted-foreground" data-testid="step-up-none">{t(strongOnly ? 'highRisk.noStrongDevice' : 'devices.stepUp.none')}</p>
                    )}
                    {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
                </div>
                <DialogFooter>
                    <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
